import { useEffect, useRef, useState, useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import { dynamicAPI } from '../services/api';
import { useSiteSettings } from '../hooks/useContent';
import { usePageSeo } from '../hooks/usePageSeo';
import { buildBreadcrumbSchema, buildOrganizationSchema } from '../utils/seo';
import Breadcrumbs from '../components/Breadcrumbs';
import EventCard from '../components/EventCard';
import BlogCard from '../components/BlogCard';
import { resolveImageDisplayUrls, IMAGE_DISPLAY_WIDTH } from '../utils/optimizeImageUrl.js';
import { extractYouTubeID } from '../utils/youtube';
import VideoModal from '../components/VideoModal';


import './Blogs.css';
import './Expeditions.css';
import './SpecialEventCollection.css';
import './SpecialEventFixes.css';

const FALLBACK_IMAGE = 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=800&q=80';

function formatBlogDate(value) {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
}

// In-memory cache so repeat visits & back-nav are instant
const _seCache = {};

function getCached(slug) {
  if (_seCache[slug]) return _seCache[slug];
  try {
    const raw = sessionStorage.getItem(`se_${slug}`);
    if (raw) { const v = JSON.parse(raw); _seCache[slug] = v; return v; }
  } catch { /* ignore */ }
  return null;
}
function setCache(slug, value) {
  _seCache[slug] = value;
  try { sessionStorage.setItem(`se_${slug}`, JSON.stringify(value)); } catch { /* ignore */ }
}

export default function SpecialEventCollection() {
  const { slug } = useParams();
  const { settings } = useSiteSettings();
  const [data, setData] = useState(() => getCached(slug));
  const [loading, setLoading] = useState(!getCached(slug));
  const [notFound, setNotFound] = useState(false);
  const [activeVideoId, setActiveVideoId] = useState(null);
  const fetchedSlug = useRef(null);

  const publicBase =
    String(settings.site_public_url || '').replace(/\/$/, '') ||
    (typeof window !== 'undefined' ? window.location.origin : '');
  const brand = settings.site_brand_name || 'Invincible NGO';

  const specialEventSchema = useMemo(() => {
    if (!data) return null;
    const org = buildOrganizationSchema(settings);
    const breadcrumbs = buildBreadcrumbSchema([
      { name: 'Expeditions', url: '/expeditions' },
      { name: data.name || 'Special Event', url: `/special-events/${encodeURIComponent(slug)}` }
    ], publicBase);
    return {
      '@context': 'https://schema.org',
      '@graph': [org, breadcrumbs]
    };
  }, [data, settings, publicBase, slug]);

  usePageSeo({
    title: data?.name ? `${data.name} | ${brand}` : `Special Event | ${brand}`,
    description: String(data?.tagline || '').trim() || settings.site_meta_description,
    canonical: publicBase && slug ? `${publicBase}/special-events/${encodeURIComponent(slug)}` : undefined,
    image: data?.images?.[0]?.url || data?.treks?.[0]?.imageUrl || undefined,
    enabled: !loading && !!data,
    schema: specialEventSchema
  });


  useEffect(() => {
    if (!slug) return;
    // Already have cached data — render immediately, refresh in background
    const cached = getCached(slug);
    if (cached) {
      setData(cached);
      setLoading(false);
      // Background revalidation (silent)
      dynamicAPI.getSpecialEventBySlug(slug)
        .then(res => {
          const fresh = res?.data?.data || null;
          if (fresh) { setData(fresh); setCache(slug, fresh); }
        })
        .catch(() => {});
      return;
    }
    // No cache — fetch and show
    if (fetchedSlug.current === slug) return;
    fetchedSlug.current = slug;
    let cancelled = false;
    setLoading(true);
    setNotFound(false);
    dynamicAPI.getSpecialEventBySlug(slug)
      .then(res => {
        if (cancelled) return;
        const d = res?.data?.data || null;
        setData(d);
        if (d) setCache(slug, d);
        else setNotFound(true);
      })
      .catch(err => {
        if (cancelled) return;
        setNotFound(err?.response?.status === 404);
        setData(null);
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [slug]);

  const heroImage = data?.images?.[0]?.url || '';
  const heroClassName = heroImage
    ? 'blog-hero-banner blog-hero-banner-with-image'
    : 'blog-hero-banner';

  if (loading) {
    return (
      <div className="special-event-page">
        <div className="special-event-hero-wrapper se-skeleton">
          <div className="special-event-hero-split" style={{ gap: '2rem', padding: '3rem 2rem' }}>
            <div className="special-event-hero-text">
              <div className="sk-block" style={{ width: '60%', height: '3rem', borderRadius: 8, marginBottom: 16 }} />
              <div className="sk-block" style={{ width: '80%', height: '1.2rem', borderRadius: 6 }} />
            </div>
            <div className="special-event-hero-bento">
              <div className="sk-block" style={{ width: '100%', height: '320px', borderRadius: 16 }} />
            </div>
          </div>
        </div>
        <section className="expeditions-section">
          <div className="sk-block" style={{ width: '200px', height: '1.8rem', borderRadius: 8, marginBottom: 24 }} />
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 24 }}>
            {[1,2,3,4].map(i => <div key={i} className="sk-block" style={{ height: '280px', borderRadius: 16 }} />)}
          </div>
        </section>
      </div>
    );
  }

  if (notFound || !data) {
    return (
      <div className="special-event-page">
        <div className="blog-hero-banner">
          <div className="blog-hero-inner">
            <h1 className="blog-hero-title">Special event not found</h1>
            <p className="blog-hero-subtitle">This link may be inactive or the event was removed.</p>
          </div>
        </div>
        <div className="special-event-empty">
          <Link to="/events" className="special-event-back-link">Browse upcoming events</Link>
        </div>
      </div>
    );
  }

  const treks = Array.isArray(data.treks) ? data.treks : [];
  const stories = Array.isArray(data.stories) ? data.stories : [];

  return (
    <div className="special-event-page">
      <div className="special-event-hero-wrapper">
        <div className="special-event-hero-split">
          <div className="special-event-hero-text">
            <Breadcrumbs
              items={[
                { label: 'Expeditions', path: '/expeditions' },
                { label: data.name || 'Special Event' }
              ]}
              className="special-event-breadcrumbs"
            />
            <h1 className="special-event-title">{data.name}</h1>
            {data.tagline ? <p className="special-event-subtitle">{data.tagline}</p> : null}
          </div>
          <div className="special-event-hero-bento">
            {data.images && data.images.length > 0 ? (
              <div className={`bento-grid bento-count-${Math.min(data.images.length, 4)}`}>
                {data.images.slice(0, 4).map((img, idx) => (
                  <div key={idx} className={`bento-item bento-item-${idx + 1}`} style={{ backgroundImage: `url(${img.url})` }}></div>
                ))}
              </div>
            ) : (
              <div className="bento-grid-empty">
                <span>No images uploaded</span>
              </div>
            )}
          </div>
        </div>
      </div>


      <section className="expeditions-section">
        <div className="expeditions-section-inner">
          <h2 className="expeditions-section-title">Treks &amp; experiences</h2>
          {treks.length === 0 ? (
            <p className="expeditions-empty-hint">No treks in this collection yet.</p>
          ) : (
            <div className="special-event-trek-grid">
              {treks.map((trek) => {
                const { src: imageUrl } = resolveImageDisplayUrls(trek.imageUrl || FALLBACK_IMAGE, {
                  width: IMAGE_DISPLAY_WIDTH.card
                });
                return (
                  <EventCard
                    key={trek.id}
                    event={{
                      id: trek.id,
                      title: trek.name,
                      slug: trek.slug,
                      imageUrl,
                      duration: trek.duration || 'TBA',
                      location: trek.location || undefined,
                      difficulty: trek.difficulty || 'Moderate',
                      shortDescription: trek.tagline || trek.description || '',
                      price: trek.price ?? null,
                      departureDates: Array.isArray(trek.departureDates) ? trek.departureDates : undefined,
                      media: Array.isArray(trek.media) ? trek.media : undefined
                    }}
                  />
                );
              })}
            </div>
          )}
        </div>
      </section>

      {data.videos && data.videos.length > 0 ? (
        <section className="expeditions-section special-event-videos">
          <div className="expeditions-section-inner">
            <h2 className="expeditions-section-title">Videos</h2>
            <div className="home-blogs-grid home-blogs-grid-start">
              {data.videos.map((vid, idx) => {
                const yid = extractYouTubeID(vid.videoId);
                if (!yid) return null;
                return (
                  <div key={vid.id || idx} className="blog-card" style={{ padding: 0, overflow: 'hidden' }}>
                    <div 
                      className="video-card-thumbnail" 
                      onClick={() => setActiveVideoId(yid)}
                      style={{ width: '100%', margin: 0, borderRadius: 0 }}
                    >
                      <img src={`https://img.youtube.com/vi/${yid}/maxresdefault.jpg`} alt="Video Thumbnail" onError={(e) => { e.target.src = `https://img.youtube.com/vi/${yid}/hqdefault.jpg`; }} />
                      <div className="video-card-play-btn">
                        <div className="video-card-play-icon"></div>
                      </div>
                    </div>
                    <div className="blog-card-content" style={{ padding: '16px' }}>
                      {vid.title && <h4 className="video-card-title" style={{ textAlign: 'left', margin: 0 }}>{vid.title}</h4>}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      ) : null}

      {stories.length > 0 ? (
        <section className="expeditions-section special-event-stories">
          <div className="expeditions-section-inner">
            <h2 className="expeditions-section-title">Stories</h2>
            <div className="home-blogs-grid home-blogs-grid-start">
              {stories.map((story) => (
                <BlogCard
                  key={story.id}
                  to={story.slug ? `/blogs/${encodeURIComponent(story.slug)}` : '/blogs'}
                  image={story.imageUrl || undefined}
                  category={story.category ? story.category.toUpperCase() : undefined}
                  title={story.title || 'Story'}
                  date={formatBlogDate(story.publishedAt)}
                  author={story.author || 'Admin'}
                  excerpt={story.excerpt || undefined}
                />
              ))}
            </div>
          </div>
        </section>
      ) : null}

      <VideoModal 
        videoId={activeVideoId} 
        onClose={() => setActiveVideoId(null)} 
      />
    </div>
  );
}
